import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * Placeholder for a list or page with nothing to show. Pass an `action`
 * (usually a Button) so the next step is one click away.
 *
 * - Nothing recorded yet: the record type's icon and an "Add …" button.
 * - Nothing matches a search or filter: `compact`, a SearchOff / FilterAltOff
 *   icon and a plain "Clear search" / "Clear filters" button.
 * - `compact` inside a section of a page (a tab, under a details panel, the
 *   charts area); full size when it is the page's main content.
 * - Whole-page problems use PageState, which wraps this with tone="error".
 */
const EmptyState = ({ icon: Icon, title, description, action, compact = false, tone = 'primary' }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      px: 3,
      py: compact ? 4 : 7,
    }}
  >
    {Icon && (
      <Box
        sx={{
          width: compact ? 48 : 64,
          height: compact ? 48 : 64,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          mb: 2,
          color: `${tone}.main`,
          bgcolor: (theme) => alpha(theme.palette[tone].main, 0.1),
        }}
      >
        <Icon sx={{ fontSize: compact ? 24 : 32 }} />
      </Box>
    )}
    <Typography variant="h6" component="p">
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mt: 0.5 }}>
        {description}
      </Typography>
    )}
    {action && (
      <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
        {action}
      </Box>
    )}
  </Box>
);

export default EmptyState;
